import { Link } from "react-router-dom";
import {
  Target, DollarSign, Gavel, Users, FileText, FileEdit, AlertTriangle, Shield,
  Navigation, Calculator, ShieldAlert, Building, UserCog, HandCoins,
  Sparkles, Scan, Send, Stethoscope, BarChart3, Scale, FolderOpen,
  Search, TrendingUp, Briefcase, Activity
} from "lucide-react";
import Footer from "@/components/shared/Footer";

const TOOL_CATEGORIES = [
  {
    title: "Intelligence Layer",
    desc: "Real-time intelligence to navigate any litigation",
    gradient: "from-slate-800 to-blue-900",
    tools: [
      { icon: Sparkles, name: "Case Starter", desc: "Quick AI case analysis & strategy", id: "case-starter" },
      { icon: Navigation, name: "Litigation GPS", desc: "Judge, counsel, carrier, court & jury pool intelligence", id: "litigation-gps" },
      { icon: DollarSign, name: "Financial Engine", desc: "Case valuation, cost forecasting, settlement optimization & ROI", id: "financial-engine" },
      { icon: Calculator, name: "Damages Calculator", desc: "Economic, non-economic & punitive damages with verdict benchmarking", id: "damages-calculator" },
      { icon: ShieldAlert, name: "Fraud Analytics", desc: "Benford's law, party linkage & communication pattern analysis", id: "fraud-analytics" },
    ]
  },
  {
    title: "Business Services",
    desc: "Corporate law, financing & partner services",
    gradient: "from-indigo-600 to-violet-700",
    tools: [
      { icon: Building, name: "Companeze", desc: "LLC formation, compliance, IP & entity advisory", id: "companeze" },
      { icon: UserCog, name: "Lawyer Partner Portal", desc: "Corporate services, referrals & contract generation", id: "partner-portal" },
      { icon: HandCoins, name: "Legal Financing", desc: "Pre-settlement funding calculator & applications", id: "legal-financing" },
    ]
  },
  {
    title: "Premium Intelligence",
    desc: "Deep case investigation & analysis",
    gradient: "from-amber-600 to-orange-600",
    tools: [
      { icon: Scan, name: "The Investigator", desc: "Party intelligence & risk analysis", id: "investigator" },
      { icon: Sparkles, name: "Litix Wizard", desc: "Guided case onboarding in 6 steps", id: "litix-wizard" },
      { icon: Send, name: "Request Center", desc: "Multi-source intelligence requests", id: "request-center" },
      { icon: Stethoscope, name: "Medical Provider Intel", desc: "Provider-attorney network detection", id: "medical-intel" },
      { icon: Target, name: "XAI Predictor", desc: "Explainable AI outcome predictions", id: "xai-predictor" },
    ]
  },
  {
    title: "Trial Preparation",
    desc: "Everything for trial readiness",
    gradient: "from-blue-600 to-indigo-600",
    tools: [
      { icon: Sparkles, name: "Opening Statements", desc: "Trial-ready opening statement generator", id: "opening" },
      { icon: Target, name: "Closing Arguments", desc: "Compelling closing argument builder", id: "closing" },
      { icon: FolderOpen, name: "Trial Organizer", desc: "Complete trial binder system", id: "trial-organizer" },
      { icon: Users, name: "Deposition Outlines", desc: "Examination questions & strategy", id: "deposition" },
      { icon: FileText, name: "Motion Practice", desc: "MSJ, dismiss, compel motions", id: "motion" },
    ]
  },
  {
    title: "Settlement & Negotiation",
    desc: "Maximize settlement outcomes",
    gradient: "from-emerald-600 to-teal-600",
    tools: [
      { icon: DollarSign, name: "Settlement Strategy", desc: "Negotiation strategy & talking points", id: "settlement-negotiation" },
      { icon: Send, name: "Demand Letters", desc: "Professional demand drafting", id: "demand-letter" },
      { icon: Scale, name: "Mediation Briefs", desc: "Position statements & strategy", id: "mediation" },
      { icon: Shield, name: "Insurance Analysis", desc: "Coverage & bad faith analysis", id: "insurance-report" },
    ]
  },
  {
    title: "Analytics & Live Data",
    desc: "Court records, patterns & trends",
    gradient: "from-purple-600 to-pink-600",
    tools: [
      { icon: Gavel, name: "Judge Analytics", desc: "Live court record patterns", id: "judges" },
      { icon: Briefcase, name: "Attorney Profiles", desc: "Practice pattern intelligence", id: "attorneys" },
      { icon: Search, name: "Precedent Finder", desc: "AI-powered case precedent analysis", id: "precedent-finder" },
      { icon: TrendingUp, name: "Trend Analyzer", desc: "Litigation trend analysis", id: "trend-analyzer" },
      { icon: BarChart3, name: "Strategy Patterns", desc: "Historical litigation benchmarks", id: "strategy" },
      { icon: Activity, name: "Matter Analytics", desc: "Procedural scoring engine", id: "analytics" },
    ]
  },
];

const FeaturesPage = () => {
  return (
    <main className="min-h-screen" style={{ background: '#F8FAFC' }}>
      <div className="container-shell py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-2.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4"
            style={{ background: '#fff', border: '1px solid #D9E4F2', color: '#416588' }}>
            <span className="w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6,#14B8A6)' }}></span>
            50+ Intelligence Modules
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#1E293B' }}>Every Tool an Attorney Needs</h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#475569' }}>
            From case intake to verdict — AI-powered intelligence, financial analysis, fraud detection, trial prep, and settlement optimization.
          </p>
        </div>

        <div className="space-y-12">
          {TOOL_CATEGORIES.map((cat) => (
            <div key={cat.title}>
              <div className={`rounded-2xl p-5 mb-4 text-white bg-gradient-to-r ${cat.gradient}`}>
                <h2 className="text-lg font-bold">{cat.title}</h2>
                <p className="text-sm opacity-80">{cat.desc}</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cat.tools.map((tool) => (
                  <Link to="/login" key={tool.id} className="block">
                    <div className="rounded-2xl p-5 bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5 h-full">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg,#dbeafe,#eff6ff)' }}>
                          <tool.icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-800">{tool.name}</h3>
                          <p className="text-xs text-slate-500 mt-0.5">{tool.desc}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-16 rounded-3xl p-8" style={{ background: '#fff', border: '1px solid #D9E4F2', boxShadow: '0 12px 30px rgba(15,23,42,.08)' }}>
          <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: '#1E293B' }}>Platform Analytics</h2>
          <div className="grid md:grid-cols-5 gap-6">
            {[
              { num: '50+', label: 'AI Tools' },
              { num: '11', label: 'Service Networks' },
              { num: '135+', label: 'Data Points per Case' },
              { num: '5', label: 'Intelligence Modules' },
              { num: '89%', label: 'Member Satisfaction' }
            ].map(({ num, label }) => (
              <div key={label} className="text-center p-4 rounded-2xl" style={{ background: 'linear-gradient(180deg,#fff,#f8fbff)', border: '1px solid #D9E4F2' }}>
                <div className="text-2xl font-extrabold" style={{ color: '#1E293B' }}>{num}</div>
                <div className="text-xs" style={{ color: '#475569' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link to="/login">
            <button className="px-8 py-3 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-colors">
              Sign In to Access All Tools
            </button>
          </Link>
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default FeaturesPage;
